import { useInfiniteQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { useAutoLoadMore } from '@/hooks/useAutoLoadMore';
import { fetchCadeParties } from '@/services/cade';
import type { CadePartyIdentity, CadePartyListItem } from '@/services/types';

const PAGE_SIZE = 50;

/**
 * Parties named on a CADE decision, searchable by name.
 *
 * The population is the SESSION ARCHIVE's 3,209 parties, not the 16,423 rows in
 * `parties`: the rest arrive from CADE's published registry and are named on no
 * tribunal decision, so listing them would offer thousands of rows leading
 * nowhere.
 *
 * **Ordered by NAME, never by decision count.** An order by volume is a ranking
 * of parties by how often the archive names them, which reads as a ranking of
 * exposure -- and a corpus whose party names were never merged cannot support
 * one: the top row would be whichever spelling happens to have accumulated the
 * most decisions, not whichever firm. The twenty digit-only parse artefacts
 * ("07:17", "18/06/2025") sort LAST and carry a badge, so the directory opens
 * on a company rather than on a clock -- and they are never dropped.
 *
 * Batches append as the reader scrolls, as on the case list: 3,209 rows is
 * 128 arrow-clicks, and every one lost the reader's place. The search is part
 * of the query key, so a new search drops every accumulated batch at once --
 * which is also why there is no `keepPreviousData` and no stale total here.
 */
export function CadePartiesPage() {
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') ?? '');

  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);

  const {
    data: paged,
    isPending,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['cade', 'parties', query],
    queryFn: ({ pageParam }) =>
      fetchCadeParties({ query: query || undefined, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    // Driven by the server's own total, never by "the last batch was full".
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, page) => n + page.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
  });
  const { sentinelRef, scrollRef, loadMore } = useAutoLoadMore({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });
  const data = paged?.pages[0];
  const items = paged?.pages.flatMap((page) => page.items) ?? [];

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('cade:parties.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('cade:parties.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="cade:parties.explainer" />
      <FieldGuide i18nKey="cade:parties.guide.list" level={2} />
      {data ? <AsOfStrip asOf={data.as_of} /> : null}

      {/* Above the list, not below it. A reader who scans party names before
          meeting this reads them as companies. */}
      {data ? (
        <section className="rounded-lg border bg-card p-4">
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('cade:parties.identity.title')}
          </h2>
          <Answer answer={data.identity} scope="corpus" help={t('cade:parties.identity.help')}>
            {(identity: CadePartyIdentity) => <IdentityNote identity={identity} />}
          </Answer>
        </section>
      ) : null}

      {/* The export replays the same search the screen ran. The directory is
          the one nav-level CADE screen that had no export; a reader who wants
          the variants of one firm side by side needs the rows, not the page. */}
      <div className="flex flex-wrap items-end gap-3">
        <label className="block flex-1 space-y-1">
          <span className="text-xs font-medium text-foreground">{t('cade:parties.search')}</span>
        <input
          type="search"
          value={query}
          // No offset to reset: the search IS the query key.
          onChange={(event) => setQuery(event.target.value)}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm"
        />
        <span className="text-xs text-muted-foreground">{t('cade:parties.searchHint')}</span>
        </label>
        <ExportButton
          path={`/api/v1/cade/export/parties${query ? `?q=${encodeURIComponent(query)}` : ''}`}
        />
      </div>

      {isPending ? (
        <SkeletonLoader rows={8} />
      ) : error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : !data ? (
        <SkeletonLoader rows={8} />
      ) : items.length === 0 ? (
        <EmptyState hint={t('cade:parties.emptyHint')} />
      ) : (
        <>
          <p
            aria-live="polite"
            data-loaded={items.length}
            data-total={data.total}
            className="text-xs text-muted-foreground"
          >
            {t('cade:parties.showing', {
              loaded: number.format(items.length),
              total: number.format(data.total),
            })}
          </p>

          <div
            ref={scrollRef}
            className="max-h-[65vh] overflow-auto rounded-lg border"
            role="region"
            aria-label={t('cade:parties.tableLabel')}
          >
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-muted text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('cade:parties.columns.name')}</th>
                  <th className="px-3 py-2 font-medium">{t('cade:parties.columns.roles')}</th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('cade:parties.columns.decisions')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('cade:parties.columns.cases')}
                  </th>
                  <th className="px-3 py-2 font-medium">{t('cade:parties.columns.years')}</th>
                  <th className="px-3 py-2 font-medium">{t('cade:parties.columns.attributed')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <PartyRow key={item.slug} item={item} />
                ))}
              </tbody>
            </table>
            <div ref={sentinelRef} aria-hidden className="h-px" />
            <div className="flex justify-center py-3">
              {hasNextPage ? (
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={isFetchingNextPage}
                  className="rounded-md border bg-background px-3 py-1.5 text-sm text-foreground disabled:opacity-40"
                >
                  {isFetchingNextPage
                    ? t('cade:cases.loadingMore')
                    : t('cade:parties.loadMore', { count: PAGE_SIZE })}
                </button>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {t('cade:parties.allLoaded', { total: number.format(data.total) })}
                </p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function PartyRow({ item }: { item: CadePartyListItem }) {
  const { t, i18n } = useTranslation(['cade', 'common']);
  const number = new Intl.NumberFormat(i18n.language);
  return (
    <tr className="border-t hover:bg-muted/30">
      <td className="px-3 py-2">
        <Link
          to={`/cade/parties/${item.slug}`}
          className="text-primary underline-offset-4 hover:underline"
        >
          {item.name}
        </Link>
        {/* A clock or a date the parse read as a name. Flagged on the row,
            not removed: the record exists and the identity panel counts it. */}
        {item.is_artefact ? (
          <span
            className="ml-2 rounded border border-amber-500/40 px-1 text-[10px] text-amber-700 dark:text-amber-400"
            title={t('cade:parties.artefactTitle')}
          >
            {t('cade:parties.artefact')}
          </span>
        ) : null}
      </td>
      <td className="px-3 py-2 text-muted-foreground">
        {item.roles.map((role) => t(`cade:parties.roles.${role}`, { defaultValue: role })).join(', ') ||
          '—'}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-foreground">
        {number.format(item.decisions)}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
        {number.format(item.cases)}
      </td>
      <td className="px-3 py-2 tabular-nums text-muted-foreground">
        {item.first_year === null || item.last_year === null
          ? '—'
          : item.first_year === item.last_year
            ? item.first_year
            : t('common:asOf.yearWindow', { first: item.first_year, last: item.last_year })}
      </td>
      {/* A flag and never an amount: the record page lists the attributions
          per decision, and nothing on this screen sums them. */}
      <td className="px-3 py-2 text-xs text-muted-foreground">
        {item.has_attributed_fine ? t('cade:parties.attributedYes') : '—'}
      </td>
    </tr>
  );
}

function IdentityNote({ identity }: { identity: CadePartyIdentity }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);

  return (
    <div className="space-y-2 text-xs">
      <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-foreground">
        <AlertTriangle
          className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400"
          aria-hidden
        />
        <p>
          {identity.resolved
            ? t('cade:cases.parties.resolved', { count: identity.merged })
            : t('cade:cases.parties.unresolved')}
        </p>
      </div>
      <dl className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <div>
          <dt className="text-muted-foreground">{t('cade:cases.parties.records')}</dt>
          <dd className="tabular-nums text-foreground">{number.format(identity.linked)}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">{t('cade:cases.parties.withCnpj')}</dt>
          <dd className="tabular-nums text-foreground">{number.format(identity.with_cnpj)}</dd>
        </div>
        {/* The other population in the same table. The directory lists the
            linked records only, and a reader should see how many rows it is
            NOT listing and why: these arrive from CADE's published register
            and appear on no tribunal decision. */}
        <div>
          <dt className="text-muted-foreground">{t('cade:cases.parties.registryOnly')}</dt>
          <dd className="tabular-nums text-foreground">
            {number.format(identity.registry_only)}
          </dd>
        </div>
      </dl>
      {identity.cnpj_cluster_conflicts ? (
        <p className="text-muted-foreground">
          {t('cade:cases.parties.cnpjConflicts', { count: identity.cnpj_cluster_conflicts })}
        </p>
      ) : null}
      {identity.artefact_names > 0 ? (
        <p className="text-muted-foreground">
          {t('cade:cases.parties.artefactNames', { count: identity.artefact_names })}
        </p>
      ) : null}
    </div>
  );
}
